import { createFileRoute, Navigate } from '@tanstack/react-router';
import { z } from 'zod';

import { searchReturnTo } from '../components/searchReturnTo';
import { useAuth } from '../context/AuthContext';
import { useEventQuery } from '../hooks/react-query/useEventsQuery';
import {
  useProjectEventsBatchGetQuery,
  type UseProjectEventsBatchGetQueryArgs,
} from '../hooks/react-query/useProjectEventsQuery';
import JobDetails from '../modules/JobDetails';

export const Route = createFileRoute('/job-details/$eventId')({
  component: RouteComponent,
  params: z.object({
    eventId: z.coerce
      .number()
      .min(0)
      .refine((val) => !isNaN(val), {
        message: 'Event ID must be a valid number',
      }),
  }),
  validateSearch: z.object({
    tab: z.string().optional(),
    /** `new` pins the field view for users whose preference is the admin view. */
    flow: z.enum(['new']).optional(),
    /** The search this job was opened from; the phone header's Back returns to it. */
    returnTo: searchReturnTo,
    /** Phones: a note from the activity feed, open as its own page. */
    note: z.coerce.number().positive().optional().catch(undefined),
    /**
     * From the dispatch card's Resend button: the refused ServiceChannel send (sc_sync_event id)
     * to offer again on the job (86bc12cgh).
     */
    scResend: z
      .union([z.string(), z.number()])
      // The router parses a bare number out of the query string as JSON, so `?scResend=1565`
      // arrives as a number; the id is compared as text.
      .transform((value) => String(value))
      .optional()
      .catch(undefined),
  }),
  head(ctx) {
    return {
      meta: [{ title: `Job Details - ${ctx.params.eventId}` }],
    };
  },
});

function RouteComponent() {
  const { eventId } = Route.useParams();
  const { tab, flow, returnTo } = Route.useSearch();
  const { user } = useAuth();

  const { data: event, isLoading: isEventLoading } = useEventQuery({
    filter: { id: eventId },
    enabled: !!eventId,
  });

  /** Event.parentId is the ProjectEvent row id (see useEnhancedProjectData). */
  const projectEventParentId = event?.parentId ?? 0;
  const shouldLoadProjectEvent = projectEventParentId > 0;

  const projectEventsBatchArgs: UseProjectEventsBatchGetQueryArgs = {
    filter: { id: projectEventParentId },
    enabled: shouldLoadProjectEvent,
  };
  const { data: projectData, isLoading: isProjectEventLoading } =
    useProjectEventsBatchGetQuery(projectEventsBatchArgs);

  const projectEventRow = projectData?.results?.[0];

  if (isEventLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (shouldLoadProjectEvent && isProjectEventLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (event && projectEventParentId > 0 && projectEventRow) {
    return <Navigate to="/project/$projectId" params={{ projectId: eventId }} replace />;
  }

  if (user.preferAdminView && flow !== 'new') {
    return (
      <Navigate to="/jobs/$jobId" params={{ jobId: eventId }} search={{ flow: 'legacy' }} replace />
    );
  }

  // Keyed so job-to-job navigation remounts the provider tree — form state, edit-section
  // registrations, and the job-info draft stash must never leak between jobs
  return <JobDetails key={eventId} eventId={eventId} initialTab={tab} returnTo={returnTo} />;
}
